import { FC } from 'react'
import { unstable_useBlocker as useBlocker, useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Widget, WidgetSimpleList } from '@factorialco/f0-react/dist/experimental'
import { Timer } from '@factorialco/f0-react/icons/app'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { MyProjectsInsightDocument, MyProjectsInsightQuery } from 'generated/resources/graphql'
import { groupBy, orderBy } from 'lodash'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'
import { buildNoQueryWidget, buildWidget } from 'modules/core/lib/widgets'

import { ProjectsUpsellRequestSources } from 'modules/project_management/components/upsells/constants'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

import './index.styles.css'
const PROJECTS_LIMIT = 5

const prepareData = (data: MyProjectsInsightQuery) => {
  const hasFlexibleTrackingFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_flexible_tracking
  )?.available
  const hasContributorsFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_contributors
  )?.available
  const currentMonth = moment()
    .format('MMMM')
    .replace(/^\w/, (c) => c.toLocaleUpperCase())

  const timeRecords = data?.projectManagement.timeRecords || []
  const globalRecords = data?.projectManagement.globalTimeRecords || []

  const records = [...timeRecords, ...globalRecords]
  const groupedData = groupBy(records, (record) => record?.projectWorker?.imputableProject?.id)

  const projects = orderBy(
    data?.projectManagement.imputableProjectsConnection.nodes.map((project) => ({
      id: project.id,
      title: project.name,
      minutes:
        (groupedData?.[project.id] ?? []).reduce<number>(
          (acc, record) => acc + (record?.imputedMinutes || 0),
          0
        ) ?? 0,
    })),
    ['minutes'],
    ['desc']
  )

  return {
    subtitle: currentMonth,
    items: projects.slice(0, PROJECTS_LIMIT).map((project) => ({
      id: project.id,
      title: project.title,
      rawTag: {
        icon: Timer,
        text: minutesToText(project.minutes),
      },
    })),
    showMoreCount:
      (data?.projectManagement.imputableProjectsConnection.totalCount || 0) - PROJECTS_LIMIT,
    isOwnerOrEditor:
      data?.projectManagement.projectsConnection.nodes[0]?._policies?.assignEditor.can,
    hasContributorsFeature,
    hasFlexibleTrackingFeature,
  }
}
type PreparedData = ReturnType<typeof prepareData>

const ProjectsWidgetComponent: FC<{
  data: PreparedData
  employeeId: string
  onClickWidget?: () => void
}> = ({
  data: {
    subtitle,
    items,
    showMoreCount,
    isOwnerOrEditor,
    hasContributorsFeature,
    hasFlexibleTrackingFeature,
  },
  employeeId,
  onClickWidget,
}) => {
  const { navigation } = useNavigation()
  const { employee: currentEmployee } = useCurrentSession()

  const getProjectsLinkUrl = (): string | null => {
    const isCurrentEmployee = employeeId === currentEmployee?.id

    if (isCurrentEmployee) {
      return hasFlexibleTrackingFeature ? navigation.myProjects.fullPath() : null
    }

    if (!isCurrentEmployee && isOwnerOrEditor) {
      return hasContributorsFeature
        ? navigation.projects.contributors.contributorId.fullPath({
            employeeId: employeeId,
          })
        : navigation.projects.fullPath()
    }

    return null
  }

  const linkUrl = getProjectsLinkUrl()

  const title =
    employeeId === currentEmployee?.id
      ? i18n.t('insights.widgets.my_projects.title')
      : i18n.t('insights.widgets.projects.title')

  const navigate = useNavigate()

  useBlocker(({ nextLocation }) => {
    if (linkUrl && linkUrl.endsWith(nextLocation.pathname)) {
      onClickWidget?.()
    }
    return false
  })

  const handleNavigate = () => {
    if (linkUrl) {
      navigate(linkUrl)
    }
  }
  const handleClickItem = (id: string | number) => {
    navigate(
      navigation.projects.id.fullPath({
        id,
      })
    )
  }

  return (
    <Widget
      header={{
        title,
        subtitle,
        link: linkUrl ? { title: title, url: linkUrl } : undefined,
      }}
      action={
        showMoreCount <= 0
          ? undefined
          : {
              label: i18n.tp('insights.widgets.projects.see_more', { count: showMoreCount }),
              onClick: handleNavigate,
            }
      }
    >
      <Box className={'Projects_container'}>
        <WidgetSimpleList items={items} onClickItem={handleClickItem} showAllItems />
      </Box>
    </Widget>
  )
}

const ProjectUpsellWidgetComponent: FC = () => {
  return (
    <Widget header={{ title: i18n.t('insights.widgets.my_projects.title') }}>
      <UpsellingKit.EmptyState
        moduleRequested={AvailableModules.projects}
        requestSource={ProjectsUpsellRequestSources.profile}
        learnMoreNavigate={{
          url: 'https://help.factorialhr.com/tasks-projects',
          isExternal: true,
        }}
        featureRequested={Features.projects_overview}
        requestReason={ValidReasons.MissingFeature}
        title={i18n.t('insights.widgets.projects.upsell.title')}
        description={i18n.t('insights.widgets.projects.upsell.description')}
        variant="card"
      />
    </Widget>
  )
}

export const buildProjectsWidget = () =>
  buildWidget({
    id: 'projects',
    document: MyProjectsInsightDocument,
    isEmpty: (data) => !data.items.length,
    prepareData,
    component: ProjectsWidgetComponent,
  })

export const buildProjectsUpsellWidget = () =>
  buildNoQueryWidget({
    id: 'project-upsell',
    component: ProjectUpsellWidgetComponent,
  })
